import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { auth } from '../firebase-config';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { isProEntitled } from '../lib/entitlement';
import { triggerDraftSave } from '../lib/draftRegistry';
import { tStatic } from '../i18n/staticT';

// Pure decision function — extracted so the interval/DOM-listener wiring
// around it doesn't need its own test. "warn" fires once inside the lead
// window before the threshold; "logout" once the threshold is reached.
export function computeInactivityState({ lastActivity, now, thresholdMs, warnLeadMs }) {
  const elapsed = now - lastActivity;
  if (elapsed >= thresholdMs) return "logout";
  if (elapsed >= thresholdMs - warnLeadMs) return "warn";
  return "active";
}

const INACTIVITY_THRESHOLD_MS = 7200000; // 2h of foreground inactivity
const INACTIVITY_WARN_LEAD_MS = 300000;  // 5 min warning before logout
const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'touchstart', 'scroll', 'wheel'];

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState(null); // State to store the user's role
  const [userData, setUserData] = useState(null);
  // Admin-only "view as" override to preview the Free/Pro dashboard. Persisted
  // so it survives reloads (the canvas re-reads the deck on load).
  const [previewTier, setPreviewTierState] = useState(() => {
    try { return localStorage.getItem('adminPreviewTier') || null; } catch (e) { return null; }
  });
  const setPreviewTier = (t) => {
    setPreviewTierState(t);
    try {
      if (t) localStorage.setItem('adminPreviewTier', t);
      else localStorage.removeItem('adminPreviewTier');
    } catch (e) { /* ignore */ }
  };

  // Fetch the users/{uid} doc and update role + userData. Returns the fresh
  // data (or null). Reused by auth-state changes AND on-demand refresh (e.g.
  // after returning from Stripe checkout, to pick up a new entitlement).
  // Stable across renders (useCallback) so effects that depend on it — e.g. the
  // /billing/success poll — don't restart on every state update.
  const loadUserDoc = useCallback(async (user) => {
    if (!user) {
      setRole(null);
      setUserData(null);
      return null;
    }
    const db = getFirestore();
    const snap = await getDoc(doc(db, 'users', user.uid));
    const data = snap.exists() ? snap.data() : null;
    if (!data) console.error('User document not found');
    setRole(data ? data.role : null);
    setUserData(data);
    return data;
  }, []);

  // Re-fetch the current user's doc on demand (no auth event needed).
  const refreshUserData = useCallback(() => loadUserDoc(auth.currentUser), [loadUserDoc]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await loadUserDoc(user);
      } else {
        setRole(null);
        setUserData(null);
        setPreviewTier(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [loadUserDoc]);

  // Inactivity auto-logout. Armed once while signed in, with a single set of
  // listeners and one interval torn down on sign-out or unmount. Only counts
  // time while the tab is in the foreground, and re-baselines when the tab
  // regains focus — so switching apps or backgrounding the tab for a while no
  // longer logs the user out on return (the previous implementation leaked a
  // fresh timer on every auth event and, because background tabs freeze
  // setTimeout, stranded its activity timestamp and logged users out mid-work).
  useEffect(() => {
    if (!currentUser) return undefined;

    let lastActivity = Date.now();
    let hasWarned = false;

    const markActive = () => { lastActivity = Date.now(); hasWarned = false; };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') { lastActivity = Date.now(); hasWarned = false; }
    };

    const check = () => {
      if (document.visibilityState !== 'visible') return;
      const state = computeInactivityState({
        lastActivity, now: Date.now(),
        thresholdMs: INACTIVITY_THRESHOLD_MS, warnLeadMs: INACTIVITY_WARN_LEAD_MS,
      });
      if (state === 'logout') {
        triggerDraftSave();
        if (auth.currentUser) signOut(auth);
        alert(tStatic('auth.inactivityLogoutAlert'));
      } else if (state === 'warn' && !hasWarned) {
        hasWarned = true;
        if (window.confirm(tStatic('auth.inactivityWarnConfirm'))) {
          lastActivity = Date.now();
          hasWarned = false;
        }
      }
    };

    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, markActive, { passive: true }));
    document.addEventListener('visibilitychange', onVisibility);
    const intervalId = setInterval(check, 60000);

    return () => {
      clearInterval(intervalId);
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, markActive));
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [currentUser]);

  const isAdmin = role === 'Admin';
  const realIsPro = isProEntitled(userData, new Date());
  // Admins can preview the Free/Pro experience via previewTier; everyone else
  // gets their real entitlement.
  const isPro = isAdmin && previewTier ? previewTier === 'Pro' : realIsPro;

  const value = {
    currentUser,
    role,
    isAdmin,
    entitlement: userData?.entitlement || null,
    subscription: userData?.subscription || null,
    isPro,
    realIsPro,
    previewTier,
    setPreviewTier,
    refreshUserData,
    userLang: userData?.lang || null,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
